import { MAX_FORM_IMAGE_BYTES, MAX_FORM_IMAGE_PX } from '~constants/common';

export interface PreparedImage {
  file: File;
  downscaled: boolean;
}

// Rung 3's long edge, half of MAX_FORM_IMAGE_PX, only reached once PNG-to-JPEG re-encoding at
// the full pixel cap has already failed to fit the byte cap.
const FALLBACK_LONG_EDGE_PX = MAX_FORM_IMAGE_PX / 2;

const readAsImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image file'));
      img.onload = () => resolve(img);
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Draws `img` onto a new canvas scaled so its long edge is at most `longEdge`. Never scales up:
// an image already under the cap is re-encoded at its own size.
const drawScaled = (img: HTMLImageElement, longEdge: number): HTMLCanvasElement => {
  const scale = Math.min(1, longEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const blobToFile = (blob: Blob, type: string): File => new File([blob], type === 'image/png' ? 'image.png' : 'image.jpg', { type });

/**
 * Downscales and re-encodes an uploaded image before it ever reaches the network, bounded by
 * `MAX_FORM_IMAGE_PX` (long edge) and `MAX_FORM_IMAGE_BYTES`. Always re-encodes, even when the
 * source is already small, so every stored object came from our own encoder and is guaranteed a
 * non-interlaced PNG or a baseline JPEG, the two formats PDFKit (and this feature) support.
 *
 * A bounded three-rung ladder, not a search:
 *   1. Fit the pixel cap, keep the source format (PNG stays PNG for transparency).
 *   2. If still over budget and the source was PNG, re-encode the same pixels as JPEG. The
 *      common bloat case, a photo saved as PNG.
 *   3. If still over budget, halve the long edge and re-encode as JPEG at a lower quality.
 *
 * Returns null when even the smallest rung cannot fit the byte cap; the caller toasts and does
 * not insert anything.
 */
export async function prepareFormImage(file: File): Promise<PreparedImage | null> {
  const img = await readAsImage(file);
  const isPng = file.type === 'image/png';
  const downscaled = Math.max(img.naturalWidth, img.naturalHeight) > MAX_FORM_IMAGE_PX;

  const rung1Canvas = drawScaled(img, MAX_FORM_IMAGE_PX);
  const rung1Type = isPng ? 'image/png' : 'image/jpeg';
  const rung1Blob = await canvasToBlob(rung1Canvas, rung1Type, isPng ? undefined : 0.85);
  if (rung1Blob && rung1Blob.size <= MAX_FORM_IMAGE_BYTES) {
    return { file: blobToFile(rung1Blob, rung1Type), downscaled };
  }

  if (isPng) {
    const rung2Blob = await canvasToBlob(rung1Canvas, 'image/jpeg', 0.85);
    if (rung2Blob && rung2Blob.size <= MAX_FORM_IMAGE_BYTES) {
      return { file: blobToFile(rung2Blob, 'image/jpeg'), downscaled: true };
    }
  }

  const rung3Canvas = drawScaled(img, FALLBACK_LONG_EDGE_PX);
  const rung3Blob = await canvasToBlob(rung3Canvas, 'image/jpeg', 0.8);
  if (rung3Blob && rung3Blob.size <= MAX_FORM_IMAGE_BYTES) {
    return { file: blobToFile(rung3Blob, 'image/jpeg'), downscaled: true };
  }

  return null;
}
